// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — ShapeLibrary (M3: V·IANet Flow)
// Left sidebar — Capa 1B compact redesign
// ═══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import type React from 'react';
import type { TipoNodoFlujograma } from '../types/flowchart.types';
import { useI18n } from '../../../../../i18n';
import { FLOW_TOKENS as T } from '../shared/flowTokens';

// ─── Shape Definitions ───────────────────────────────────────────────────────

interface ShapeItem {
  type: TipoNodoFlujograma;
  labelKey: string;
  preview: React.ReactNode;
}

function MiniPreview({ children }: { children: React.ReactNode }) {
  return (
    <svg width={18} height={14} viewBox="0 0 18 14" style={{ flexShrink: 0 }}>
      {children}
    </svg>
  );
}

const SHAPES: ShapeItem[] = [
  {
    type: 'ACTIVIDAD',
    labelKey: 'tipoActividad',
    preview: (
      <MiniPreview>
        <rect
          x={1}
          y={2}
          width={16}
          height={10}
          rx={2}
          fill="none"
          stroke={T.color.textTertiary}
          strokeWidth={0.8}
        />
      </MiniPreview>
    ),
  },
  {
    type: 'DECISION',
    labelKey: 'tipoDecision',
    preview: (
      <MiniPreview>
        <polygon
          points="9,1 17,7 9,13 1,7"
          fill="none"
          stroke={T.color.textTertiary}
          strokeWidth={0.8}
        />
      </MiniPreview>
    ),
  },
  {
    type: 'DOCUMENTO',
    labelKey: 'tipoDocumento',
    preview: (
      <MiniPreview>
        <path
          d="M3 2h12v9c-2-1-4 1-6 0s-4-1-6 0V2z"
          fill="none"
          stroke={T.color.textTertiary}
          strokeWidth={0.8}
        />
      </MiniPreview>
    ),
  },
  {
    type: 'SUBPROCESO',
    labelKey: 'tipoSubproceso',
    preview: (
      <MiniPreview>
        <rect
          x={1}
          y={2}
          width={16}
          height={10}
          rx={1}
          fill="none"
          stroke={T.color.textTertiary}
          strokeWidth={0.8}
        />
        <line x1={4} y1={2} x2={4} y2={12} stroke={T.color.textTertiary} strokeWidth={0.4} />
        <line x1={14} y1={2} x2={14} y2={12} stroke={T.color.textTertiary} strokeWidth={0.4} />
      </MiniPreview>
    ),
  },
  {
    type: 'INICIO',
    labelKey: 'tipoInicio',
    preview: (
      <MiniPreview>
        <rect
          x={1}
          y={2}
          width={16}
          height={10}
          rx={5}
          fill="none"
          stroke={T.color.textTertiary}
          strokeWidth={0.8}
        />
      </MiniPreview>
    ),
  },
  {
    type: 'FIN',
    labelKey: 'tipoFin',
    preview: (
      <MiniPreview>
        <rect
          x={1}
          y={2}
          width={16}
          height={10}
          rx={5}
          fill="none"
          stroke={T.color.textTertiary}
          strokeWidth={0.8}
        />
        <rect
          x={3}
          y={4}
          width={12}
          height={6}
          rx={3}
          fill="none"
          stroke={T.color.textTertiary}
          strokeWidth={0.5}
        />
      </MiniPreview>
    ),
  },
  {
    type: 'PROCESO_REF',
    labelKey: 'procesoRef',
    preview: (
      <MiniPreview>
        <circle cx={9} cy={7} r={6} fill="none" stroke={T.color.textTertiary} strokeWidth={0.8} />
      </MiniPreview>
    ),
  },
  {
    type: 'PROCESO_GOBIERNO',
    labelKey: 'gobCorporativo',
    preview: (
      <MiniPreview>
        <circle cx={9} cy={7} r={6} fill="none" stroke={T.color.textTertiary} strokeWidth={0.8} />
        <circle cx={9} cy={7} r={4} fill="none" stroke={T.color.textTertiary} strokeWidth={0.5} />
      </MiniPreview>
    ),
  },
  {
    type: 'RESULTADO',
    labelKey: 'tipoResultado',
    preview: (
      <MiniPreview>
        <rect
          x={1}
          y={2}
          width={16}
          height={10}
          rx={5}
          fill="none"
          stroke={T.color.textTertiary}
          strokeWidth={0.8}
        />
      </MiniPreview>
    ),
  },
];

// ─── Drag Handler ────────────────────────────────────────────────────────────

function onDragStart(event: React.DragEvent, type: TipoNodoFlujograma) {
  event.dataTransfer.setData('application/reactflow', type);
  event.dataTransfer.effectAllowed = 'move';
}

// ─── Shape Button (M8: only shape, no text, tooltip on hover) ───────────────

function ShapeBtn({ shape, label }: { shape: ShapeItem; label: string }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, shape.type)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 30,
        borderRadius: 3,
        border: `1px solid ${hovered ? T.color.secondary : T.color.border}`,
        backgroundColor: hovered ? '#F0FBFF' : 'transparent',
        cursor: 'grab',
        transition: 'all 0.12s',
        userSelect: 'none',
      }}
      title={label}
    >
      {shape.preview}
    </div>
  );
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface ShapeLibraryProps {
  onAddPhase?: (name: string) => void;
  phases?: { id: string; name: string; width: number }[];
  onRenamePhase?: (id: string, name: string) => void;
  onRemovePhase?: (id: string) => void;
  onResizePhase?: (id: string, direction: 'left' | 'right') => void;
}

// ─── Phase Item (M7) ────────────────────────────────────────────────────────

function PhaseItem({
  phase,
  onRename,
  onRemove,
  onResize,
  isFirst,
  isLast,
}: {
  phase: { id: string; name: string; width: number };
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  onResize?: (id: string, direction: 'left' | 'right') => void;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  const { t } = useI18n();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(phase.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setValue(phase.name);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isEditing, phase.name]);

  const save = () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== phase.name) {
      onRename(phase.id, trimmed);
    }
    setIsEditing(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '3px 4px',
        borderRadius: 3,
        border: `1px solid ${T.color.border}`,
        fontSize: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {isEditing ? (
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') setIsEditing(false);
            }}
            onBlur={save}
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              fontSize: '8px',
              fontFamily: T.font.family,
              padding: '1px 2px',
            }}
          />
        ) : (
          <span
            onClick={() => setIsEditing(true)}
            style={{
              flex: 1,
              fontFamily: T.font.family,
              color: T.color.textSecondary,
              fontWeight: 500,
              cursor: 'text',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={t.flow.clicRenombrar}
          >
            {phase.name}
          </span>
        )}
        <button
          onClick={() => !isEditing && setIsEditing(true)}
          style={{
            width: 14,
            height: 14,
            borderRadius: 2,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: T.color.textTertiary,
            fontSize: '8px',
            lineHeight: 1,
          }}
          title={t.flow.renombrarFase}
        >
          ✎
        </button>
        <button
          onClick={() => onRemove(phase.id)}
          style={{
            width: 14,
            height: 14,
            borderRadius: 2,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: T.color.textTertiary,
            fontSize: '10px',
            lineHeight: 1,
          }}
          title={t.flow.eliminarFase}
        >
          ×
        </button>
      </div>
      {onResize && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center' }}>
          <button
            onClick={() => onResize(phase.id, 'left')}
            disabled={isFirst}
            style={{
              width: 16,
              height: 14,
              borderRadius: 2,
              border: `1px solid ${T.color.border}`,
              background: isFirst ? T.color.surfaceAlt : 'transparent',
              cursor: isFirst ? 'default' : 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isFirst ? T.color.textTertiary : T.color.textSecondary,
              fontSize: '8px',
              opacity: isFirst ? 0.4 : 1,
            }}
            title={t.flow.expandirIzquierda}
          >
            ◀
          </button>
          <span
            style={{
              fontSize: '7px',
              fontFamily: T.font.family,
              color: T.color.textTertiary,
              minWidth: 24,
              textAlign: 'center',
            }}
          >
            {phase.width}
          </span>
          <button
            onClick={() => onResize(phase.id, 'right')}
            disabled={isLast}
            style={{
              width: 16,
              height: 14,
              borderRadius: 2,
              border: `1px solid ${T.color.border}`,
              background: isLast ? T.color.surfaceAlt : 'transparent',
              cursor: isLast ? 'default' : 'pointer',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isLast ? T.color.textTertiary : T.color.textSecondary,
              fontSize: '8px',
              opacity: isLast ? 0.4 : 1,
            }}
            title={t.flow.expandirDerecha}
          >
            ▶
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ShapeLibrary({
  onAddPhase,
  phases,
  onRenamePhase,
  onRemovePhase,
  onResizePhase,
}: ShapeLibraryProps) {
  const { t } = useI18n();
  const [addingPhase, setAddingPhase] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState('');
  const phaseInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (addingPhase && phaseInputRef.current) {
      phaseInputRef.current.focus();
    }
  }, [addingPhase]);

  const handleAddPhaseSubmit = () => {
    const trimmed = newPhaseName.trim();
    if (trimmed && onAddPhase) {
      onAddPhase(trimmed);
    }
    setNewPhaseName('');
    setAddingPhase(false);
  };

  return (
    <div
      style={{
        width: 160,
        backgroundColor: T.color.surface,
        borderRight: `1px solid ${T.color.border}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '8px 10px',
          borderBottom: `1px solid ${T.color.border}`,
        }}
      >
        <h3
          style={{
            fontSize: '8px',
            fontWeight: 700,
            fontFamily: T.font.family,
            color: T.color.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.7px',
            margin: 0,
          }}
        >
          {t.flow.formas}
        </h3>
      </div>

      {/* Shape List (M8: grid of icon-only buttons) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, padding: '6px 6px' }}>
        {SHAPES.map((shape) => (
          <ShapeBtn
            key={shape.type}
            shape={shape}
            label={(t.flow as unknown as Record<string, string>)[shape.labelKey] ?? shape.labelKey}
          />
        ))}
      </div>

      {/* Phases section */}
      <div
        style={{
          padding: '6px 10px',
          borderTop: `1px solid ${T.color.border}`,
          marginTop: 4,
        }}
      >
        <h4
          style={{
            fontSize: '8px',
            fontWeight: 700,
            fontFamily: T.font.family,
            color: T.color.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            margin: '0 0 4px 0',
          }}
        >
          {t.flow.fases}
        </h4>

        {/* M7: Phase list */}
        {phases && phases.length > 0 && onRenamePhase && onRemovePhase && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 6 }}>
            {phases.map((phase, idx) => (
              <PhaseItem
                key={phase.id}
                phase={phase}
                onRename={onRenamePhase}
                onRemove={onRemovePhase}
                onResize={onResizePhase}
                isFirst={idx === 0}
                isLast={idx === phases.length - 1}
              />
            ))}
          </div>
        )}

        {addingPhase ? (
          <div style={{ display: 'flex', gap: 3 }}>
            <input
              ref={phaseInputRef}
              value={newPhaseName}
              onChange={(e) => setNewPhaseName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddPhaseSubmit();
                if (e.key === 'Escape') {
                  setNewPhaseName('');
                  setAddingPhase(false);
                }
              }}
              onBlur={handleAddPhaseSubmit}
              placeholder={t.flow.nombrePlaceholder}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '3px 6px',
                borderRadius: 3,
                border: `1px solid ${T.color.secondary}`,
                fontSize: '8px',
                fontFamily: T.font.family,
                outline: 'none',
              }}
            />
          </div>
        ) : (
          <button
            onClick={() => setAddingPhase(true)}
            style={{
              width: '100%',
              padding: '4px 8px',
              borderRadius: 3,
              border: `1px dashed ${T.color.border}`,
              backgroundColor: 'transparent',
              cursor: 'pointer',
              fontSize: '8px',
              fontWeight: 600,
              fontFamily: T.font.family,
              color: T.color.textTertiary,
              transition: 'all 0.12s',
            }}
            title={t.flow.agregarFase}
          >
            {t.flow.agregarFase}
          </button>
        )}
      </div>

      {/* Instructions */}
      <div
        style={{
          marginTop: 'auto',
          padding: '6px 10px',
          borderTop: `1px solid ${T.color.border}`,
        }}
      >
        <p
          style={{
            fontSize: '7px',
            fontFamily: T.font.family,
            color: T.color.textTertiary,
            lineHeight: 1.4,
            margin: 0,
          }}
        >
          {t.flow.arrastrarForma}
        </p>
      </div>
    </div>
  );
}
